import '../../product/product.scss';
import { ReactNode, useContext, useState } from 'react';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { DeleteAction, EditAction } from '../../../components/actions/actions';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { Button } from '../../../components/form/button';
import { toast } from '../../../util/toasts';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import Icon from '../../../components/icons/icon';
import { TenantContext } from '../../../context/tenant-context';
import { BreadCrumb } from '../../../components/breadcrumbs/breadcrumbs';
import { TagPricing, TagPricingSearchRequest } from '../../../clients/quipli-service/user/user';

export const TagsList = () => {
  const { searchTagPricings, deleteTagPricing } = useAPI();

  const [isListLoading, setListLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [actionModal, setActionModal] = useState<ReactNode>();
  const { contextPath } = useContext(TenantContext);
  const [pagination, setPagination] = useState<PaginationData>();

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<TagPricingSearchRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    (_request) => {
      setListLoading(true);

      const { keyword, pageSize, pageNumber } = _request;
      searchTagPricings({
        pageSize,
        pageNumber,
        keyword,
        hideDeleted: true,
      })
        .then(({ pagination, items }) => {
          setPagination(pagination);
          setData(items.map((item) => buildDataRow(item)));
        })
        .catch(() => {
          toast('Failed to load tags', 'error');
        })
        .finally(() => {
          setListLoading(false);
        });
    },
  );

  // Vars
  const columns: DataTableColumn[] = [
    { key: 'name', label: 'Name' },
    { key: 'discount', label: 'Discount' },
    { key: 'eligibility', label: 'Eligibility' },
    { key: 'rentalLimit', label: 'Rental Limit' },
    { key: 'buttons', label: '', fixedAsLast: true },
  ];

  //Helpers
  const buildDataRow = (p: TagPricing) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {p.name}
          </Typography>
        </>
      ),
      fields: {
        name: (
          <div className="product-container">
            <Typography type="text-sm" className="text-[#1D252C]">
              {p.name}
            </Typography>
          </div>
        ),
        discount: (
          <div className="product-container">
            <Typography type="text-sm" className="text-[#1D252C]">
              {Number(p.discount).toFixed(2)}%
            </Typography>
          </div>
        ),
        eligibility: (
          <div>
            <Eligibility productIds={p.productIds} categoryIds={p.categoryIds} />
          </div>
        ),
        rentalLimit: (
          <Typography type="text-sm" className="text-[#1D252C]">
            {p.rentalLimit}
          </Typography>
        ),

        buttons: (
          <div className="button-group">
            <EditAction
              onClick={() => {
                const url = `${contextPath}manager/tagpricing/form/${p.id}`;
                window.open(url, '_self')?.focus();
              }}
              data-cy="edit_tag"
            />

            <DeleteAction
              onClick={() =>
                setActionModal(
                  <ConfirmModal
                    isOpen={true}
                    close={() => setActionModal(undefined)}
                    onConfirm={async () => {
                      try {
                        await deleteTagPricing(p.id.toString());
                        await fetchCallback();
                        toast('Discount deleted successfully', 'success');
                      } catch (error) {
                        toast('Failed to delete discount', 'error');
                      } finally {
                        setActionModal(undefined);
                      }
                    }}
                    headerIcon={<i className="fa fa-trash-can delete-icon"></i>}
                    prompt={`Are you sure you want to delete discount ${p.name}?`}
                  />,
                )
              }
              data-cy="delete_discount"
            ></DeleteAction>
          </div>
        ),
        id: p.id,
      },
    };
  };

  const CreateDiscount = () => (
    <Button
      className="button-yellow md w-[max-content]"
      data-cy="add_tags"
      onClick={() => {
        const url = `${contextPath}manager/tagpricing/form`;
        window.open(url, '_self')?.focus();
      }}
    >
      <Typography type="text-md" weight="regular" className="text-[var(--quipli-black-800)]">
        Create Discount
      </Typography>
      <Icon icon="Plus" size="medium" stroke="var(--quipli-black-800)" />
    </Button>
  );

  return (
    <div className="product-list">
      <div className="container-v2">
        <BreadCrumb breadcrumbs={[{ label: 'Customers' }, { label: 'Global Customer Discounts' }]} />

        <div className="flex justify-between items-center">
          <Typography type="display-sm" weight="medium">
            Global Customer Discounts
          </Typography>

          <div className="flex md:hidden">
            <CreateDiscount />
          </div>
        </div>
        <Typography type="text-md" weight="regular" className="text-grey-500">
          Create and manage percentage-based discounts that can be assigned to customers.
        </Typography>

        <div className="flex items-start sm:items-center py-5 justify-between search-bar-padding flex-col sm:flex-row mt-2">
          <SearchBar
            placeholder="Search discounts"
            defaultValue={request.keyword}
            setRequest={setRequest}
            data-cy="search_tags"
            className="w-full"
          />
          <div>
            <div className="hidden md:flex flex-end">
              <div className="flex gap-2 h-[fit-content]"></div>
              <div className="pl-2">
                <CreateDiscount />
              </div>
            </div>
          </div>
        </div>

        <DataTable
          isLoading={isListLoading}
          columnNames={columns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          cypressPrevious="previous_tags_page"
          cypressNext="next_tags_page"
        />

        {actionModal}
      </div>
    </div>
  );
};

export const Eligibility = ({ productIds, categoryIds }: { productIds: number[]; categoryIds: number[] }) => {
  let label = 'All products';

  let productsStr = '',
    categoriesStr = '';

  if (productIds.length > 0) {
    productsStr = `${productIds.length} product${productIds.length > 1 ? 's' : ''}`;
  }
  if (categoryIds.length > 0) {
    categoriesStr = `${categoryIds.length} categor${categoryIds.length > 1 ? 'ies' : 'y'}`;
  }
  const nonBlankStr = [productsStr, categoriesStr].filter((s) => s != '').join(' • ');

  label = nonBlankStr || 'All products';

  return (
    <div>
      <Typography type="text-sm" className="text-[#1D252C]" data-cy="eligibility">
        {label}
      </Typography>
    </div>
  );
};
